import { CloudVmsApi } from 'clients';
import { type IEvent } from 'clients/PlatformApi';
import useSWR from 'swr';
import * as EventUtil from 'utils/EventUtil';

/**
 * Fetches the clip view share link for the event.
 *
 * Only fetches the link on component mount when browser is Safari.
 * Safari has a limitation that it only allows copying to clipboard within
 * user events, like click or pointerdown.
 * So for Safari, we pre-fetch the share links for the clips that are in view
 * (there aren't too many thanks to our recycler list mechanism).
 * For other browsers, after the user clicks the button, we make an API call
 * to get the URL first then in the callback we copy the URL to the clipboard.
 * https://webkit.org/blog/10247/new-webkit-features-in-safari-13-1
 */
export default function useClipViewShareLink(event: IEvent, isSafari: boolean) {
  const isShared = EventUtil.isShared(event) && EventUtil.isPushToCloudComplete(event);
  const { id: eventId } = event;
  const selfShare = EventUtil.getSelfShare(event);

  const { data, mutate } = useSWR(
    isSafari && isShared && selfShare?.id
      ? ['clipViewShareLink', eventId, selfShare.id, event.shares, event.pushToCloudStatus]
      : null,
    async ([, eventId, shareId]) => {
      try {
        const res = await CloudVmsApi().getSharedViewLink({ eventId, shareId });
        return res?.url;
      } catch (error) {
        console.warn('Failed to fetch clip view share link', error);
      }
    },
    {
      revalidateOnFocus: false,
    },
  );

  return {
    clipViewShareLink: data,
    mutateClipViewShareLink: mutate,
  };
}
